import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Firma, Komentar, ApiClientService, Djelatnost_firme, Drzava, Grad, Oblast_firme, Status_firme, Artikl, Sms_sort } from '../../services/api-client.service';
import { CacheService } from '../../services/cache.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-firme',
  templateUrl: './firme.component.html',
  styleUrls: ['./firme.component.scss']
})
export class FirmeComponent {
  @ViewChild('firmeGrid', { static: false }) firmeGrid: any;
  @ViewChild('komentariGrid', { static: false }) komentariGrid: any;

  // Main grid data
  gridData: Firma[] = [];
  loading = false;

  // Popup states
  isFirmaPopupVisible = false;
  isKomentariPopupVisible = false;
  isCreateFirma = false;

  // Selected items
  selectedFirma: Firma | null = null;
  selectedKomentar: Komentar | null = null;

  // Komentari data
  komentariData: Komentar[] = [];
  komentariLoading = false;

  // Load panels
  loadPanelPosition = { of: '#firmeGrid' };
  komentariLoadPanelPosition = { of: '#komentariGrid' };

  // Forms
  firmaForm!: FormGroup;
  komentarForm!: FormGroup;

  users: any[] = [];
  usersLoading = false;
  currentUser: any = null;

  oblasti: Oblast_firme[] = [];
  statusi: Status_firme[] = [];
  djelatnosti: Djelatnost_firme[] = [];
  gradovi: Grad[] = [];
  drzave: Drzava[] = [];
  artikli: Artikl[] = [];

  isSmsSortPopupVisible = false;
  smsSortData: Sms_sort[] = [];
  smsSortLoading = false;
  selectedSmsSort: Sms_sort | null = null;
  smsSortLoadPanelPosition = { of: '#smsSortGrid' };

  // SMS Sort form
  smsSortForm!: FormGroup;

  domacaOptions = [
    { value: 0, text: 'Ne' },
    { value: 1, text: 'Da' }
  ];

  artiklSmsSortEditorOptions: any = {
    dataSource: this.artikli,
    displayExpr: 'artikl1',
    valueExpr: 'id',
    placeholder: 'Odaberite artikl...',
    searchEnabled: true
  };

  constructor(
    private apiClient: ApiClientService,
    private cache: CacheService,
    private fb: FormBuilder,
    private userService: UserService
  ) {
    this.getUsername = this.getUsername.bind(this);
    this.getArtiklName = this.getArtiklName.bind(this);
    this.getDjelatnostName = this.getDjelatnostName.bind(this);
    this.getDrzavaName = this.getDrzavaName.bind(this);
    this.getGradName = this.getGradName.bind(this);
    this.getOblastName = this.getOblastName.bind(this);
    this.getStatusName = this.getStatusName.bind(this);
    this.initializeForms();
  }

  async ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    await this.loadUsers();
    await this.loadLookupData();
    await this.loadFirme();
  }

  async loadLookupData() {
    try {
      const [oblastiData, statusiData, djelatnostiData, gradoviData, drzaveData, artikliData] = await Promise.all([
        await this.cache.getCachedOrFetch('oblasti', () => this.apiClient.oblastiFirmeAll()),
        await this.cache.getCachedOrFetch('statusi', () => this.apiClient.statusiFirmeAll()),
        await this.cache.getCachedOrFetch('djelatnosti', () => this.apiClient.djelatnostiFirmeAll()),
        await this.cache.getCachedOrFetch('gradovi', () => this.apiClient.gradoviAll()),
        await this.cache.getCachedOrFetch('drzave', () => this.apiClient.drzaveAll()),
        await this.cache.getCachedOrFetch('artikli', () => this.apiClient.artikliAll())
      ]);

      this.oblasti = oblastiData;
      this.statusi = statusiData;
      this.djelatnosti = djelatnostiData;
      this.gradovi = gradoviData;
      this.drzave = drzaveData;
      this.artikli = artikliData;
    } catch (error) {
      console.error('Error loading lookup data:', error);
    }
  }

  async loadUsers() {
    this.loading = true;
    this.usersLoading = true;
    try {
      this.users = await this.cache.getCachedOrFetch('logins', () => this.apiClient.loginsAll());
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      this.usersLoading = false;
    }
  }

  initializeForms() {
    // Firma form
    this.firmaForm = this.fb.group({
      naziv: ['', Validators.required],
      oblast: [null],
      status: [null],
      djelatnost: [null],
      telefon1: [''],
      telefon2: [''],
      fax: [''],
      mobilni: [''],
      mobilni_sms: [''],
      direktor: [''],
      kontakt1: [''],
      kontakt2: [''],
      adresa: [''],
      grad: [null],
      drzava: [null],
      racun: [''],
      pdv_br: [''],
      oib_br: [''],
      email: ['', Validators.email],
      web: [''],
      datum_kontakta: [null],
      period_od: [null],
      period_do: [null],
      domaca: [0]
    });

    // Komentar form
    this.komentarForm = this.fb.group({
      komentar1: ['', Validators.required],
      idKorisnika: [null],
      sifraFirme: [null],
      datum: [new Date().toISOString().split('T')[0]] // Date only in YYYY-MM-DD format
    });

    this.smsSortForm = this.fb.group({
      artikl: [null, Validators.required]
    });
  }

  async loadFirme(invalidateCache = false) {
    this.loading = true;
    try {
      var firmeData: Firma[];
      if (invalidateCache) {
        this.cache.remove('firme');
        firmeData = await this.apiClient.firmeAll(10000, 0);
      }
      else {
        firmeData = await this.cache.getCachedOrFetch('firme', () => this.apiClient.firmeAll(10000, 0));
      }
      this.gridData = firmeData;
    } catch (error) {
      console.error('Error loading firme:', error);
    } finally {
      this.loading = false;
    }
  }

  getOblastName(rowData: any): string {
    const oblastId = rowData.oblast;
    if (!oblastId) return '';
    const oblast = this.oblasti.find(o => o.id === oblastId);
    return oblast ? oblast.oblast as string : '';
  }

  getStatusName(rowData: any): string {
    const statusId = rowData.status;
    if (!statusId) return '';
    const status = this.statusi.find(s => s.id === statusId);
    return status ? status.status as string : '';
  }

  getDjelatnostName(rowData: any): string {
    const djelatnostId = rowData.djelatnost;
    if (!djelatnostId) return '';
    const djelatnost = this.djelatnosti.find(d => d.id === djelatnostId);
    return djelatnost ? djelatnost.djelatnost as string : '';
  }

  getGradName(rowData: any): string {
    const gradId = rowData.grad;
    if (!gradId) return '';
    const grad = this.gradovi.find(g => g.id === gradId);
    return grad ? grad.grad1 as string : '';
  }

  getDrzavaName(rowData: any): string {
    const drzavaId = rowData.drzava;
    if (!drzavaId) return '';
    const drzava = this.drzave.find(d => d.id === drzavaId);
    return drzava ? drzava.naziv as string : '';
  }

  async loadKomentari(firmaId: number) {
    this.komentariLoading = true;
    try {
      // Load users and komentari in parallel
      const [komentari, users] = await Promise.all([
        this.apiClient.komentariAll(firmaId),
        this.users.length === 0 ? this.loadUsers() : Promise.resolve()
      ]);
      this.komentariData = komentari.reverse();;
    } catch (error) {
      console.error('Error loading komentari:', error);
    } finally {
      this.komentariLoading = false;
    }
  }

  // FIRMA CRUD OPERATIONS
  onFirmaRowClick(e: any) {
    if (e.data) {
      this.selectedFirma = e.data;
    }
  }

  onAddFirmaClick() {
    this.selectedFirma = null;
    this.isCreateFirma = true;
    this.firmaForm.reset();
    this.isFirmaPopupVisible = true;
  }

  async onEditFirmaClick() {
    if (this.selectedFirma) {
      this.isCreateFirma = false;
      this.populateFirmaForm(this.selectedFirma);

      // Load komentari when editing
      if (this.users.length === 0) {
        await this.loadUsers();
      }
      await this.loadKomentari(this.selectedFirma.id as number);

      this.isFirmaPopupVisible = true;
    }
  }


  onDeleteFirmaClick() {
    if (this.selectedFirma && confirm('Da li ste sigurni da želite obrisati ovu firmu?')) {
      this.deleteFirma(this.selectedFirma.id as number);
    }
  }

  populateFirmaForm(firma: Firma) {
    this.firmaForm.patchValue({
      naziv: firma.naziv,
      oblast: firma.oblast,
      status: firma.status,
      djelatnost: firma.djelatnost,
      telefon1: firma.telefon1,
      telefon2: firma.telefon2,
      fax: firma.fax,
      mobilni: firma.mobilni,
      mobilni_sms: firma.mobilni_sms,
      direktor: firma.direktor,
      kontakt1: firma.kontakt1,
      kontakt2: firma.kontakt2,
      adresa: firma.adresa,
      grad: firma.grad,
      drzava: firma.drzava,
      racun: firma.racun,
      pdv_br: firma.pdv_br,
      oib_br: firma.oib_br,
      email: firma.email,
      web: firma.web,
      datum_kontakta: firma.datum_kontakta,
      period_od: firma.period_od,
      period_do: firma.period_do,
      domaca: firma.domaca
    });
  }

  async createFirma() {
    if (this.firmaForm.valid) {
      this.loading = true;
      try {
        const formData = this.firmaForm.value;
        formData.datum_kontakta = formData.datum_kontakta ? new Date(formData.datum_kontakta).toISOString().split('T')[0] : null;
        formData.period_od = formData.period_od ? new Date(formData.period_od).toISOString().split('T')[0] : null;
        formData.period_do = formData.period_do ? new Date(formData.period_do).toISOString().split('T')[0] : null;
        formData.sifra = "10"; // Default value for new firms
        await this.apiClient.firmePOST(formData);
        this.isFirmaPopupVisible = false;
        this.firmaForm.reset();
        this.loadFirme(true); // Refresh grid
      } catch (error) {
        console.error('Error creating firma:', error);
      } finally {
        this.loading = false;
      }
    }
  }

  async updateFirma() {
    if (this.firmaForm.valid && this.selectedFirma) {
      this.loading = true;
      try {
        const formData = this.firmaForm.value;
        formData.datum_kontakta = formData.datum_kontakta ? new Date(formData.datum_kontakta).toISOString().split('T')[0] : null;
        formData.period_od = formData.period_od ? new Date(formData.period_od).toISOString().split('T')[0] : null;
        formData.period_do = formData.period_do ? new Date(formData.period_do).toISOString().split('T')[0] : null;
        formData.sifra = "10";
        formData.id = this.selectedFirma.id;
        await this.apiClient.firmePUT(this.selectedFirma.id as number, formData);
        this.isFirmaPopupVisible = false;
        this.loadFirme(true); // Refresh grid
      } catch (error) {
        console.error('Error updating firma:', error);
      } finally {
        this.loading = false;
      }
    }
  }

  async deleteFirma(id: number) {
    this.loading = true;
    try {
      await this.apiClient.firmeDELETE(id);
      this.selectedFirma = null;
      this.loadFirme(true); // Refresh grid
    } catch (error) {
      console.error('Error deleting firma:', error);
    } finally {
      this.loading = false;
    }
  }

  // KOMENTARI CRUD OPERATIONS
  onKomentarRowClick(e: any) {
    if (e.data) {
      this.selectedKomentar = e.data;
    }
  }

  onAddKomentarClick() {
    this.selectedKomentar = null;
    this.komentarForm.reset();
    this.komentarForm.patchValue({
      sifraFirme: this.selectedFirma?.id,
      datum: new Date()
    });
  }

  // onEditKomentarClick() {
  //   if (this.selectedKomentar) {
  //     this.populateKomentarForm(this.selectedKomentar);
  //   }
  // }

  // onDeleteKomentarClick() {
  //   if (this.selectedKomentar && confirm('Da li ste sigurni da želite obrisati ovaj komentar?')) {
  //     this.deleteKomentar(this.selectedKomentar.id as number);
  //   }
  // }

  populateKomentarForm(komentar: Komentar) {
    this.komentarForm.patchValue({
      komentar1: komentar.komentar1,
      idKorisnika: komentar.idKorisnika,
      sifraFirme: komentar.sifraFirme,
      datum: komentar.datum
    });
  }

  async createKomentar() {
    if (this.komentarForm.valid && this.selectedFirma) {
      this.komentariLoading = true;
      try {
        const formData = this.komentarForm.value;
        formData.sifraFirme = this.selectedFirma.id;
        formData.idKorisnika = this.currentUser.id;
        formData.datum = new Date().toISOString().split('T')[0];

        await this.apiClient.komentariPOST(formData);

        // Reset form and refresh komentari
        this.komentarForm.reset();
        await this.loadKomentari(this.selectedFirma.id as number);

      } catch (error) {
        console.error('Error creating komentar:', error);
      } finally {
        this.komentariLoading = false;
      }
    }
  }

  getUsername(komentar: any): string {
    if (!komentar.idKorisnika || !this.users) return 'Nepoznato';
    const user = this.users.find(u => u.id === komentar.idKorisnika);
    return user ? user.username : `Korisnik #${komentar.idKorisnika}`;
  }

  async updateKomentar() {
    if (this.komentarForm.valid && this.selectedKomentar) {
      this.komentariLoading = true;
      try {
        const formData = this.komentarForm.value;
        formData.id = this.selectedKomentar.id;
        formData.datum = new Date().toISOString().split('T')[0];
        await this.apiClient.komentariPUT(this.selectedKomentar.id as number, formData);
        this.komentarForm.reset();
        this.loadKomentari(this.selectedFirma!.id as number); // Refresh komentari
      } catch (error) {
        console.error('Error updating komentar:', error);
      } finally {
        this.komentariLoading = false;
      }
    }
  }

  async deleteKomentar(id: number) {
    this.komentariLoading = true;
    try {
      await this.apiClient.komentariDELETE(id);
      this.selectedKomentar = null;
      this.loadKomentari(this.selectedFirma!.id as number); // Refresh komentari
    } catch (error) {
      console.error('Error deleting komentar:', error);
    } finally {
      this.komentariLoading = false;
    }
  }

  // Popup handlers
  onFirmaPopupHidden() {
    this.isFirmaPopupVisible = false;
    this.selectedFirma = null;
    this.komentariData = []; // Clear komentari when popup closes
    this.komentarForm.reset(); // Reset komentar form
  }

  onSmsSortClick() {
    if (this.selectedFirma) {
      this.loadSmsSorts(this.selectedFirma.id as number);
      this.isSmsSortPopupVisible = true;
    }
  }

  async loadSmsSorts(firmaId: number) {
    this.smsSortLoading = true;
    try {
      this.smsSortData = await this.apiClient.smsSortsAll(firmaId);
    } catch (error) {
      console.error('Error loading SMS sorts:', error);
    } finally {
      this.smsSortLoading = false;
    }
  }

  onAddSmsSortClick() {
    this.selectedSmsSort = null;
    this.smsSortForm.reset();
  }

  onEditSmsSortClick() {
    if (this.selectedSmsSort) {
      this.populateSmsSortForm(this.selectedSmsSort);
    }
  }

  onDeleteSmsSortClick() {
    if (this.selectedSmsSort && confirm('Da li ste sigurni da želite obrisati ovaj SMS sort?')) {
      this.deleteSmsSort(this.selectedSmsSort.id as number);
    }
  }

  populateSmsSortForm(smsSort: Sms_sort) {
    this.smsSortForm.patchValue({
      artikl: smsSort.artikl
    });
  }

  async createSmsSort() {
    if (this.smsSortForm.valid && this.selectedFirma) {
      this.smsSortLoading = true;
      try {
        const formData = this.smsSortForm.value;
        formData.firma = this.selectedFirma.id;
        await this.apiClient.smsSortsPOST(formData);
        this.smsSortForm.reset();
        this.loadSmsSorts(this.selectedFirma.id as number); // Refresh SMS sorts
      } catch (error) {
        console.error('Error creating SMS sort:', error);
      } finally {
        this.smsSortLoading = false;
      }
    }
  }

  async deleteSmsSort(id: number) {
    this.smsSortLoading = true;
    try {
      await this.apiClient.smsSortsDELETE(id);
      this.selectedSmsSort = null;
      this.loadSmsSorts(this.selectedFirma!.id as number); // Refresh SMS sorts
    } catch (error) {
      console.error('Error deleting SMS sort:', error);
    } finally {
      this.smsSortLoading = false;
    }
  }

  onSmsSortPopupHidden() {
    this.isSmsSortPopupVisible = false;
    this.selectedSmsSort = null;
    this.smsSortData = [];
  }

  onSmsSortRowClick(e: any) {
    if (e.data) {
      this.selectedSmsSort = e.data;
    }
  }

  getArtiklName(smsSort: Sms_sort): string {
    return smsSort.artikl1?.artikl1 as string;
    // console.log('Getting artikl name for ID:', artiklId);
    // if (!artiklId || !this.artikli) return 'Nepoznato';
    // const artikl = this.artikli.find(a => a.id === artiklId);
    // return artikl ? artikl.artikl1 as string : `Artikl #${artiklId}`;
  }

  public isUserAgroModerator(): boolean {
    return this.userService.isUserAgroModerator();
  }
}
